import makePlainReducer from '../makePlainReducer';

/**
 * Creates a selected reducer. A `selected` reducer a single item id. It is
 * intended to be used for when a user has selected something and you need to
 * access that information in more than one place. While not technically
 * required, it's highly recommend you use the [byId](#makebyidreducer) reducer
 * in addition to this one.
 *
 * * **Initial State**: `null`
 * * **Supported Actions**:
 *   * `SELECT_<MODEL>`: Sets the state to hold the given id.
 *     * `action.id`: The id of the selected item
 *   * `DESELECT_<MODEL>`: Resets the reducer to the initial state.
 *   * `REMOVE_TOKEN`: Resets the reducer to the initial state.
 *
 * @param {Object} options The options for the creator function
 * @param {string} options.model The name of the model being used
 * @param {Object} options.customConfig Custom action handlers
 * @returns {Function} A newly created redux reducer
 *
 * @public
 * @author Connor Taylor
 * @example
 * import { combineReducers } from 'redux';
 *
 * const reducer = combineReducers({
 *   selected: makeSelectedReducer({
 *     model: `article`,
 *     customConfig: {
 *       CUSTOM_ACTION: (state, action) => `foo`,
 *     },
 *   }),
 * });
 *
 * // An example of how the selected reducer might look in practice
 * {
 *   article: {
 *     selected: 1,
 *     byId: {
 *       1: { id: 1, url: `http://www.google.com/foo`, publishDate: `2018-01-15`, featured: false },
 *       2: { id: 2, url: `http://www.google.com/bar`, publishDate: `2018-01-16` featured: true },
 *     }
 *   }
 * }
 */
const makeSelectedReducer = ({
  model,
  customConfig = {},
  initialState = null,
}) => {
  const upperModel = model.toUpperCase();

  return makePlainReducer({
    model,
    config: {
      [`SELECT_${upperModel}`]: (state, action) => action.id,
      [`DESELECT_${upperModel}`]: () => null,
      REMOVE_TOKEN: () => initialState,
      ...customConfig,
    },
    initialState,
  });
};

/** @component */
export default makeSelectedReducer;
